import { DebounceFunctionsStorage } from "@rightioltd/debounce-throttle-npm";
import { logger } from "@services/workers/helpers";
import type { IndexableType } from "dexie";
import CancellationTokenStore from "../tokens";
import { MainDatabase } from "./IndexedDB";
import type { DelayedWrite } from "./model";
import type {
	CheckIfStoreExistsInIndexedDB,
	ClearAllStorage,
	ClearCACHEStorage,
	ClearIndexedDBStore,
	ClearSTATEStorage,
	ClearStorage,
	ClearUIStorage,
	ClearVALIDStorage,
	DeleteValuesFromStoreInIndexedDB,
	GetAllValuesFromStoreInIndexedDB,
	GetFromStorage,
	GetValueFromStoreInIndexedDB,
	RemoveFromStorage,
	ReplaceValuesInStoreInIndexedDB,
	SaveToStorage,
	SetValueInStoreInIndexedDB,
	SetValuesInStoreInIndexedDB,
} from "./model/index";
/** Provides a class for manipulating browser storage
 * @remarks This class provides various static utility methods for data manipulation.
 * To use simply import AddressUtils from this file. Because this class is a static class,
 * it is not required to instantiate it. Methods use interfaces to define the input and
 * output parameters. Interfaces are defined in model/index.ts Some of the methods are async,
 * and therefore return promises. Some of the async methods are using the API, and also return promises.
 * Methods which may compute a result, sometimes modify instances of the other objects passed to them.
 * These objects are passed by reference, and are therefore modified by the method.
 * This is to modify application state.
 * @author Adam Rzymski
 * @date 28/03/2022
 * @version 1.0.0
 * @public
 * @class StorageUtils
 * @property {string} _storageType - The type of storage to use.
 * @example
 * import { StorageUtils } from "./utilities/storage/index";
 */

export class StorageUtils {
	// Public Properties
	// --------------------------------------------------------------------------
	public static cancellationTokens: CancellationTokenStore =
		new CancellationTokenStore();
	public static nameSpace: string = "StorageUtils";
	public static delayedWritesStore = new DebounceFunctionsStorage();
	public static indexedDBsStore: MainDatabase = new MainDatabase();

	// --------------------------------------------------------------------------

	// --------------------------------------------------------------------------
	// Public methods
	// --------------------------------------------------------------------------

	/**
	 * Static method to delay a write to the browser storage by a given amount of time
	 * provides debouncing for writes to the browser storage to reduce the number of writes
	 * @remarks Delays a write to the browser storage by a given amount of time. This
	 * provides a way to batch writes to the browser storage. It is a type of debouncing,
	 * which means that it will only write to the browser storage once the given amount of
	 * time has passed. This is useful for reducing the number of writes to the browser
	 * storage.
	 * @param {{ type: string, key: string, value: string, time: number }}
	 * @static
	 * @memberof StorageUtils
	 * @method delayedWrite
	 * @example
	 * // delay a write to the browser storage
	 * StorageUtils.delayedWrite({
	 * type: 'local',
	 * key: 'test',
	 * value: 'test',
	 * time: 1000
	 * });
	 * @returns {void}
	 * returns nothing
	 */

	public static delayedWrite: DelayedWrite = ({
		key,
		type,
		store,
		value,
		delay,
	}) => {
		// add or replace the debounced function
		this.delayedWritesStore.addOrExecute({
			name: type + store + key,
			delay,
			callback: () => {
				this.saveToStorage({ key, type, store, value });
			},
		});
	};

	/**
	 * Static method to save a value to the browser storage
	 * @param {{ type: string, store: string, key: string, value: any }}
	 * @static
	 * @memberof StorageUtils
	 * @method saveToStorage
	 * @example
	 * // save a value to the browser storage
	 * StorageUtils.saveToStorage({
	 * type: 'local',
	 * store: 'test',
	 * key: 'test',
	 * value: 'test'
	 * });
	 * @returns {void}
	 * returns nothing
	 */

	public static saveToStorage: SaveToStorage = ({
		type,
		store,
		key,
		value,
	}) => {
		// use switch to save to the correct storage
		switch (type) {
			// save to local storage
			case "local":
				localStorage.setItem(`${store}-${key}`, JSON.stringify(value));
				break;

			// save to session storage
			case "session":
				sessionStorage.setItem(`${store}-${key}`, JSON.stringify(value));
				break;

			// save to indexedDB
			// case "indexedDB":

			// default to local storage
			default:
				localStorage.setItem(`${store}-${key}`, JSON.stringify(value));
				break;
		}
	};

	public static openIndexedDB: () => Promise<void> = async () => {
		await this.indexedDBsStore.open();
	};

	/**
	 * Static method to get a value from the browser storage
	 * @param {{ type: string, store: string, key: string }}
	 * @static
	 * @memberof StorageUtils
	 * @method getFromStorage
	 * @example
	 * // get a value from the browser storage
	 * StorageUtils.getFromStorage({
	 * type: 'local',
	 * store: 'test',
	 * key: 'test'
	 * });
	 * @returns {any}
	 * returns the value from the browser storage
	 */

	public static getFromStorage: GetFromStorage = ({ type, store, key }) => {
		switch (type) {
			case "local":
				{
					// get data from local storage
					const data = localStorage.getItem(store + "-" + key);
					if (data) {
						// if data type is of data is string, parse it to JSON
						if (typeof data === "string" && data !== "undefined")
							return JSON.parse(data);
					} else {
						return undefined;
					}
				}
				break;
			case "session":
				{
					// get data from session storage
					const data = sessionStorage.getItem(store + "-" + key);
					if (data) {
						// if data type is of data is string, parse it to JSON
						if (typeof data === "string" && data !== "undefined")
							return JSON.parse(data);
					} else {
						return undefined;
					}
				}
				break;
			default:
				{
					// get data from local storage
					const data = localStorage.getItem(store + "-" + key);
					if (data) {
						// if data type is of data is string, parse it to JSON
						if (typeof data === "string" && data !== "undefined")
							return JSON.parse(data);
					} else {
						return undefined;
					}
				}
				break;
		}
	};

	/**
	 * Static method to remove a value from the browser storage
	 * @param {{ type: string, store: string, key: string }}
	 * @static
	 * @memberof StorageUtils
	 * @method removeFromStorage
	 * @example
	 * // remove a value from the browser storage
	 * StorageUtils.removeFromStorage({ type, store, key });
	 */

	public static removeFromStorage: RemoveFromStorage = ({
		type,
		store,
		key,
	}) => {
		// log the remove
		logger.debug(
			this.nameSpace + "->" + this.removeFromStorage.name,
			"Removing from storage",
			{ type, store, key },
		);
		// use switch to remove from the correct storage
		switch (type) {
			// remove from local storage
			case "local":
				localStorage.removeItem(store + "-" + key);
				break;
			// remove from session storage
			case "session":
				sessionStorage.removeItem(store + "-" + key);
				break;

			// default to local storage
			default:
				localStorage.removeItem(store + "-" + key);
				break;
		}
	};

	/**
	 * Static method to clear the browser storage
	 * @param {{ type: string, store: string }}
	 * @static
	 * @memberof StorageUtils
	 * @method clearStorage
	 * @example
	 * // clear the browser storage
	 * StorageUtils.clearStorage({ type, store });
	 * @returns {void}
	 * returns nothing
	 */

	public static clearStorage: ClearStorage = ({ type, store }) => {
		// log the clear
		logger.debug(
			this.nameSpace + "->" + this.clearStorage.name,
			"Clearing storage",
			{ type, store },
		);
		// use switch to clear the correct storage
		switch (type) {
			// clear local storage
			case "local":
				localStorage.clear();
				break;

			// clear session storage
			case "session":
				sessionStorage.clear();
				break;

			// default to local storage
			default:
				localStorage.clear();
				break;
		}
	};

	/**
	 * Static method to clear all browser storage
	 * @static
	 * @memberof StorageUtils
	 * @method clearAllStorage
	 * @example
	 * // clear all browser storage
	 * StorageUtils.clearAllStorage();
	 * @returns {void}
	 * returns nothing
	 */

	public static clearAllStorage: ClearAllStorage = () => {
		// log the clear
		logger.debug(
			this.nameSpace + "->" + this.clearAllStorage.name,
			"Clearing all storage",
		);

		// clear all storage
		localStorage.clear();
		sessionStorage.clear();
	};

	/**
	 * Static method to clear the browser storage
	 * @static
	 * @memberof StorageUtils
	 * @method clearUIStorage
	 * @example
	 * // clear the browser storage
	 * StorageUtils.clearUIStorage();
	 * @returns {void}
	 * returns nothing
	 */

	public static clearUIStorage: ClearUIStorage = () => {
		// log the clear
		logger.debug(
			this.nameSpace + "->" + this.clearUIStorage.name,
			"Clearing UI storage",
		);

		// clear UI storage by removing all items that start with "ui-"
		for (const item in localStorage) {
			if (item.startsWith("ui-")) localStorage.removeItem(item);
		}
	};

	/**
	 * Static method to clear the browser storage
	 * @static
	 * @memberof StorageUtils
	 * @method clearSTATEStorage
	 * @example
	 * // clear the browser storage
	 * StorageUtils.clearSTATEStorage();
	 * @returns {void}
	 * returns nothing
	 */

	public static clearSTATEStorage: ClearSTATEStorage = () => {
		// log the clear
		logger.debug(
			this.nameSpace + "->" + this.clearSTATEStorage.name,
			"Clearing STATE storage",
		);

		// clear STATE storage by removing all items that start with "state-"
		for (const item in localStorage) {
			if (item.startsWith("state-")) localStorage.removeItem(item);
		}
	};

	/**
	 * Static method to clear the browser storage
	 * @static
	 * @memberof StorageUtils
	 * @method clearCACHEStorage
	 * @example
	 * // clear the browser storage
	 * StorageUtils.clearCACHEStorage();
	 * @returns {void}
	 * returns nothing
	 */

	public static clearCACHEStorage: ClearCACHEStorage = () => {
		// log the clear
		logger.debug(
			this.nameSpace + "->" + this.clearCACHEStorage.name,
			"Clearing CACHE storage",
		);

		// clear CACHE storage by removing all items that start with "cache-"
		for (const item in localStorage) {
			if (item.startsWith("cache-")) localStorage.removeItem(item);
		}
	};

	/**
	 * Static method to clear the browser storage
	 * @static
	 * @memberof StorageUtils
	 * @method clearVALIDStorage
	 * @example
	 * // clear the browser storage
	 * StorageUtils.clearVALIDStorage();
	 * @returns {void}
	 * returns nothing
	 */

	public static clearVALIDStorage: ClearVALIDStorage = () => {
		// log the clear
		logger.debug(
			this.nameSpace + "->" + this.clearVALIDStorage.name,
			"Clearing VALID storage",
		);

		// clear VALID storage by removing all items that start with "valid-"
		for (const item in localStorage) {
			if (item.startsWith("valid-")) localStorage.removeItem(item);
		}
	};

	/**
	 * Static method to clear an IndexedDB store
	 * @param {{ name: string, store: string }}
	 * @static
	 * @memberof StorageUtils
	 * @async
	 * @method clearIndexedDBStore
	 * @example
	 * // clear an indexedDB store
	 * await StorageUtils.clearIndexedDBStore({ name, store });
	 * @returns {void}
	 * returns nothing
	 */

	public static clearIndexedDBStore: ClearIndexedDBStore = async ({
		tableName,
	}) => {
		// log the clear
		logger.debug(
			this.nameSpace + "->" + this.clearIndexedDBStore.name,
			"Clearing indexedDB store",
			{ tableName },
		);

		// clear the store
		const database = this.indexedDBsStore.getTableByName(tableName);
		await database?.clear();
	};

	/**
	 * Static method to get all values from a store in an IndexedDB
	 * @param {{ name: string, store: string }}
	 * @static
	 * @memberof StorageUtils
	 * @async
	 * @method getAllValuesFromStoreInIndexedDB
	 * @example
	 * // get all values from a store in an indexedDB
	 * await StorageUtils.getAllValuesFromStoreInIndexedDB({ name, store });
	 * @returns {Promise<any[]>}
	 * returns all values from a store in an indexedDB
	 */

	public static getAllValuesFromStoreInIndexedDB: GetAllValuesFromStoreInIndexedDB =
		async ({ tableName }: { tableName: IndexableType }) => {
			// log the get
			logger.debug(
				this.nameSpace + "->" + this.getAllValuesFromStoreInIndexedDB.name,
				"Getting all values from a store in an indexedDB",
				{ tableName },
			);

			// get all values from the store
			if (this.indexedDBsStore.getTableByName(tableName)) {
				const values = await this.indexedDBsStore
					.getTableByName(tableName)
					.toArray();
				// return the values
				return values;
			}
			// return nothing
			return [];
		};

	/**
	 * Static method to get a value from a store in an IndexedDB
	 * @param {{ name: string, store: string, key: string }}
	 * @static
	 * @memberof StorageUtils
	 * @async
	 * @method getValueFromStoreInIndexedDB
	 * @example
	 * // get a value from a store in an indexedDB
	 * await StorageUtils.getValueFromStoreInIndexedDB({ name, store, key });
	 * @returns {Promise<any>}
	 * returns a value from a store in an indexedDB
	 */

	public static getValueFromStoreInIndexedDB: GetValueFromStoreInIndexedDB =
		async ({
			tableName,
			key,
		}: {
			tableName: IndexableType;
			key: string | number;
		}) => {
			// log the get
			logger.debug(
				this.nameSpace + "->" + this.getValueFromStoreInIndexedDB.name,
				"Getting a value from a store in an indexedDB",
				{ tableName, key },
			);

			// get the value from the store
			const value = await this.indexedDBsStore
				.getTableByName(tableName)
				.get(key);

			// return the value
			return value;
		};

	/**
	 * Static method to set a value in a store in an IndexedDB
	 * @param {{ name: string, store: string, value: any }}
	 * @static
	 * @memberof StorageUtils
	 * @async
	 * @method setValueInStoreInIndexedDB
	 * @example
	 * // set a value in a store in an indexedDB
	 * await StorageUtils.setValueInStoreInIndexedDB({ name, store, key, value });
	 * @returns {Promise<void>}
	 * returns nothing
	 */

	public static setValueInStoreInIndexedDB: SetValueInStoreInIndexedDB =
		async ({ tableName, value }) => {
			// log the set
			logger.debug(
				this.nameSpace + "->" + this.setValueInStoreInIndexedDB.name,
				"Setting a value in a store in an indexedDB",
				{ tableName, value },
			);

			// set the value in the store
			await this.indexedDBsStore.getTableByName(tableName).put(value);
		};

	/**
	 * Static method to set values in a store in an IndexedDB
	 * @param {{ name: string, store: string, values: any[] }}
	 * @static
	 * @memberof StorageUtils
	 * @async
	 * @method setValuesInStoreInIndexedDB
	 * @example
	 * // set values in a store in an indexedDB
	 * await StorageUtils.setValuesInStoreInIndexedDB({ name, store, values });
	 * @returns {Promise<void>}
	 * returns nothing
	 */

	public static setValuesInStoreInIndexedDB: SetValuesInStoreInIndexedDB =
		async ({ tableName, values }) => {
			// log the set
			logger.debug(
				this.nameSpace + "->" + this.setValuesInStoreInIndexedDB.name,
				"Setting values in a store in an indexedDB",
				{ tableName, values },
			);

			// set the values in the store
			await this.indexedDBsStore.getTableByName(tableName).bulkPut(values);
		};

	/**
	 * Static method to replace all values in a store in an IndexedDB
	 * @param {{ store: IndexableType, values: any[] }}
	 * @static
	 * @memberof StorageUtils
	 * @async
	 * @method replaceValuesInStoreInIndexedDB
	 * @example
	 * // replace all values in a store in an indexedDB
	 * await StorageUtils.replaceValuesInStoreInIndexedDB({ name, store, values });
	 * @returns {Promise<void>}
	 * returns nothing
	 */

	public static replaceValuesInStoreInIndexedDB: ReplaceValuesInStoreInIndexedDB =
		async ({ tableName, values }) => {
			// log the set
			logger.debug(
				this.nameSpace + "->" + this.replaceValuesInStoreInIndexedDB.name,
				"Replacing all values in a store in an indexedDB",
				{ tableName, values },
			);

			// replace the values in the store
			const database = this.indexedDBsStore.getTableByName(tableName);
			await database?.clear();
			await database.bulkAdd(values);
		};

	/**
	 * Static method to delete values from a store in an IndexedDB
	 * @param {{ store: IndexableType}}
	 * @static
	 * @memberof StorageUtils
	 * @async
	 * @method deleteValuesFromStoreInIndexedDB
	 * @example
	 * // delete values from a store in an indexedDB
	 * await StorageUtils.deleteValuesFromStoreInIndexedDB({ name, store });
	 * @returns {Promise<void>}
	 * returns nothing
	 */

	public static deleteValuesFromStoreInIndexedDB: DeleteValuesFromStoreInIndexedDB =
		async ({ tableName }) => {
			// log the delete
			logger.debug(
				this.nameSpace + "->" + this.deleteValuesFromStoreInIndexedDB.name,
				"Deleting values from a store in an indexedDB",
				{ tableName },
			);

			// delete the values from the store
			const database = this.indexedDBsStore.getTableByName(tableName);
			await database?.clear();
		};

	/**
	 * Static method to check if store with name exists in IndexedDB
	 * @param {{ name: string, store: string}}
	 * @static
	 * @memberof StorageUtils
	 * @async
	 * @method checkIfStoreExistsInIndexedDB
	 * @example
	 * // check if store with name exists in IndexedDB
	 * await StorageUtils.checkIfStoreExistsInIndexedDB({ name, store });
	 * @returns {Promise<boolean>}
	 * returns true if store exists, false if not
	 * @throws {Error}
	 * throws an error if the store does not exist
	 */

	public static checkIfStoreExistsInIndexedDB: CheckIfStoreExistsInIndexedDB =
		async ({ tableName }) => {
			// log the check
			logger.debug(
				this.nameSpace + "->" + this.checkIfStoreExistsInIndexedDB.name,
				"Checking if store with name exists in IndexedDB",
				{ tableName },
			);

			// check if the store exists
			const exists = await this.indexedDBsStore
				.getTableByName(tableName)
				.count();

			// return the result
			return !!exists;
		};

	public static updateLocalStorage = (
		prefix: string,
		version: string,
	): void => {
		// Check if the specific key exists in localStorage
		if (
			localStorage.getItem(`${prefix}-Version`) === null ||
			localStorage.getItem(`${prefix}-Version`) !== version
		) {
			console.log(
				`Local storage ${prefix} is out of date. Clearing and updating to version ${version}`,
			);
			// Iterate through all keys in localStorage
			for (let i = 0; i < localStorage.length; i++) {
				// Get the key at the current index
				const key = localStorage.key(i);

				// Check if the key starts with the specified prefix
				if (key && key.startsWith(prefix)) {
					// Remove the key from localStorage
					localStorage.removeItem(key);

					// Since the length of localStorage has changed, adjust the index
					i--;
				}
			}

			// Set the version in localStorage
			localStorage.setItem(`${prefix}-Version`, version);
			console.log(`Local storage ${prefix} updated to version ${version}`);
			return;
		}
		console.log(
			`Local storage ${prefix} is already up to date with version ${version}`,
		);
	};

	// Usage example
}

declare global {
	interface Window {
		StorageUtils: StorageUtils;
	}
}

window.StorageUtils = StorageUtils;

export default StorageUtils;
